import { useEffect, useRef } from 'react'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { embedSession } from 'utils/embedSession/embedSession'
import { markOrderFlowLoadReported, type OrderFlowLoadFailureReason, type OrderFlowSurface } from 'utils/analytics/orderFlowLoad'
import { supportsContainerQueries } from '../purchaseFlowLayout'

/** What the surface rendered first: the steps, the consent overlay, a permission wall, an order recap, or an error. */
export type OrderFlowFirstScreen = 'steps' | 'overlay' | 'no_permission' | 'order' | 'error'

interface UseOrderFlowLoadedArgs {
  surface: OrderFlowSurface
  /** True once the first real screen of the surface is rendered (not a spinner). */
  ready: boolean
  isEmbed: boolean
  firstScreen: OrderFlowFirstScreen
  /** Order flow: the attempt id, carried in the body so the anchor event never depends on super-property timing. */
  orderAttemptId?: string
  /** Status view only: the order the card was opened for. */
  orderId?: number | null
  /** Set when the surface rendered an error screen instead of its content. */
  failureReason?: OrderFlowLoadFailureReason | null
}

/**
 * The spec's "Order Flow Loaded" (ISSUE-3218): the first screen actually rendered, once per surface mount.
 * Its counterpart, order_flow_auth_result, is fired server-side by the session exchange, so a hard render
 * failure shows as the gap "auth result success, no loaded". In the embed, load_time_ms is the latency from
 * the exchange to this render, handed out once per exchange by the session (an in-SPA re-entry has none).
 */
export const useOrderFlowLoaded = ({ surface, ready, isEmbed, firstScreen, orderAttemptId, orderId, failureReason }: UseOrderFlowLoadedArgs) => {
  const firedRef = useRef(false)

  useEffect(() => {
    if (!ready || firedRef.current) return
    firedRef.current = true
    const loadTimeMs = isEmbed ? embedSession.claimFirstScreenLatency() : null
    logAnalyticsEvent(AnalyticsEvent.ORDER_FLOW_LOADED, {
      surface,
      load_status: failureReason ? 'failure' : 'success',
      first_screen: firstScreen,
      ...(failureReason ? { failure_reason: failureReason } : {}),
      ...(loadTimeMs !== null ? { load_time_ms: loadTimeMs } : {}),
      ...(orderAttemptId ? { orderAttemptId } : {}),
      ...(orderId != null ? { orderId } : {}),
      viewportWidth: window.innerWidth,
      supportsContainerQueries: supportsContainerQueries(),
    })
    markOrderFlowLoadReported(surface)
  }, [ready, surface, isEmbed, firstScreen, orderAttemptId, orderId, failureReason])
}
